import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet } from 'react-native';

import {
  BodyText,
  Icon,
  type IconName,
  SHOULD_USE_LEGACY_ELEVATION,
  theme,
  usePressableState,
} from '@sg/garnish';

import { useLoyaltyAvailablePoints, useLoyaltyPremiumStatus } from '../../../../hooks';

export const HomeLoyaltyButton = (props: HomeLoyaltyButtonProps) => {
  const { fallbackMessage, onPress } = props;

  // ─── Interactive Style ────────────────────────────────────────────────────

  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);
  const interactingStyle = isInteracting ? styles.containerHover : null;

  // ─── Premium Icon ─────────────────────────────────────────────────────────

  const { isPremium } = useLoyaltyPremiumStatus();
  const icon: IconName = isPremium ? 'IconSgRewardBlackFill' : 'IconSgRewardSweetCornBlackOutline';

  // ─── Messaging ────────────────────────────────────────────────────────────

  const { formatMessage, formatNumber } = useIntl();
  const points = useLoyaltyAvailablePoints();
  const message =
    points > 0 ? formatMessage(messages.points, { points: formatNumber(points) }) : fallbackMessage;

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={ref}
      style={[styles.container, interactingStyle]}
      aria-label={formatMessage(messages.a11yLabel)}
      accessibilityRole="link"
      onPress={onPress}
    >
      <Icon name={icon} width={40} height={40} />

      <BodyText style={styles.message} bold sizeMatch={['16']}>
        {message}
      </BodyText>
    </Pressable>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  points: {
    defaultMessage: '{points} points',
    description: 'Home | Loyalty Button | Points',
  },
  a11yLabel: {
    defaultMessage: 'Loyalty home page',
    description: 'Home | Loyalty Button | Accessibility Label',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.OATMEAL,
    padding: theme.spacing['1'],
    paddingRight: theme.spacing['3'],
    height: theme.spacing['12'],
    borderRadius: theme.radius.xlarge,
    gap: theme.spacing['1'],
    boxShadow: [{ offsetX: 0, offsetY: 3, blurRadius: 5, color: 'rgba(14, 21, 14, 0.25)' }],
    ...(SHOULD_USE_LEGACY_ELEVATION ? { elevation: 5 } : {}),
  },
  containerHover: {
    backgroundColor: theme.colors.OATMEAL_HOVER,
  },
  message: {
    marginTop: theme.spacing['1'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type HomeLoyaltyButtonProps = {
  fallbackMessage: string;
  onPress: () => void;
};
